// Checkbox — the tri-state boolean-input primitive, pure.
//
// A thin wrapper over @radix-ui/react-checkbox: Radix owns the hard a11y
// (button semantics, role, aria-checked incl. "mixed", keyboard, disabled
// wiring) so we never hand-roll it. This file spends its effort on the
// look (foundations v1) and on keeping the public surface honest.
//
// Register (.claude/research/design/06): near-monochrome. The checked box is a solid
// NEUTRAL fill (--color-fg), not the accent — the accent stays reserved for
// the one primary action / the agent's identity (§1); a page can hold many
// checkboxes without spending it. No resting border on the box: an ink-tint
// fill marks the unchecked control and deepens on hover, an edge is revealed
// by the focus ring on :focus-visible (the border→elevation discipline, §2).
//
// Colour is never the sole channel (SC 1.4.1): the three states are told
// apart by GLYPH — checkmark (checked), dash (indeterminate), empty
// (unchecked) — not by fill colour, so the control reads without colour.
//
// Pure/props-driven: `checked` is fully controlled and every change is
// reported UP via `onCheckedChange`. Radix's callback carries the tri-state
// CheckedState, but a user click only ever yields a boolean (indeterminate
// is a programmatic-only inbound state); we narrow with a typeof guard —
// no `as` cast — so the public callback stays the honest `(boolean) => void`.
//
// Labelling: pass `label` and it is bound to the box via `id`/htmlFor, so the
// label text is also a click target (enlarging the hit area past the 24px
// floor). A Checkbox rendered WITHOUT `label` must be given an accessible
// name by the caller (a wrapping Field, aria-label, or aria-labelledby).

import * as RadixCheckbox from "@radix-ui/react-checkbox";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Checkbox.css";

// The tri-state value. Mirrors Radix's CheckedState (the spec of this
// control): a plain boolean, or "indeterminate" for the mixed state of a
// parent whose children are partly selected. Not a taggable object — the
// three states ARE these three values — so it is named and consumed as-is.
type CheckboxChecked = boolean | "indeterminate";

export type CheckboxProps = {
    // Fully controlled: the box shows exactly this and never self-mutates.
    readonly checked: CheckboxChecked;
    // Every toggle is reported up. A user interaction only ever produces a
    // boolean (checked ⇆ unchecked); "indeterminate" is inbound-only.
    readonly onCheckedChange: (checked: boolean) => void;
    readonly disabled?: boolean;
    // Optional bound label; when omitted the caller must supply an
    // accessible name (see the header note).
    readonly label?: ReactNode;
    // Ties the box and its <label> together, and is the control's DOM id.
    readonly id: string;
};

// Checkmark — the non-colour channel for the checked state (shape, not hue).
// Inherits currentColor and is decorative to AT (state is on the box's role).
const checkGlyph = <Icon name="check" />;

// Dash — the mixed/indeterminate state's own glyph, distinct from the check.
const dashGlyph = <Icon name="minus" />;

export const Checkbox = ({
    checked,
    onCheckedChange,
    disabled,
    label,
    id,
}: CheckboxProps) => (
    <span className="bilda-checkbox">
        <RadixCheckbox.Root
            id={id}
            checked={checked}
            disabled={disabled}
            // Narrow the tri-state callback to the boolean the public API
            // promises — no cast; "indeterminate" out is dropped (it never
            // arrives from a click, only from programmatic `checked`).
            onCheckedChange={(next) => {
                if (typeof next === "boolean") onCheckedChange(next);
            }}
            className="bilda-checkbox__box"
        >
            <RadixCheckbox.Indicator className="bilda-checkbox__indicator">
                {checked === "indeterminate" ? dashGlyph : checkGlyph}
            </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
        {label != null ? (
            <label htmlFor={id} className="bilda-checkbox__label">
                {label}
            </label>
        ) : null}
    </span>
);
